import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Play, RotateCcw } from 'react-feather';
import MultiSelect from './MultiSelect';
import ReportDateInput from './ReportDateInput';

/**
 * Config-driven V3 filter bar for reports.
 *
 * Fixes legacy bugs structurally:
 *  - auto-generated unique ids + correct htmlFor (no more duplicate
 *    id="startdate" / mismatched labels)
 *  - built-in date-range validation (end >= start) via field roles
 *  - Generate disabled while `loading` (no double-submit)
 *
 * Props:
 *   fields    — array of:
 *       { key, label, type:'date'|'select'|'multiselect',
 *         options?: [{value,label,sub?}], placeholder?, span?: 1|2|'full',
 *         max?, role?: 'startDate'|'endDate', disabled?: bool }
 *   values    — { [key]: value }   (multiselect values are arrays)
 *   onChange  — (key, value) => void
 *   onGenerate— () => void  (called only when validation passes)
 *   onReset   — () => void
 *   loading   — bool
 *   generateLabel — default "Generate report"
 */
const ReportFilterBar = ({
    fields = [],
    values = {},
    onChange,
    onGenerate,
    onReset,
    loading = false,
    generateLabel = 'Generate report',
}) => {
    const { t } = useTranslation();
    const idBase = useId();
    const [dateError, setDateError] = useState('');

    const { startKey, endKey } = useMemo(() => {
        let s = null, e = null;
        fields.forEach((f) => {
            if (f.role === 'startDate') s = f.key;
            if (f.role === 'endDate') e = f.key;
        });
        return { startKey: s, endKey: e };
    }, [fields]);

    const submit = (e) => {
        e.preventDefault();
        if (loading) return;
        if (startKey && endKey && values[startKey] && values[endKey] && values[endKey] < values[startKey]) {
            setDateError(t('End date cannot be earlier than start date.'));
            return;
        }
        setDateError('');
        onGenerate?.();
    };

    const fieldId = (key) => `${idBase}-${key}`;

    const renderControl = (f) => {
        const id = fieldId(f.key);
        const val = values[f.key];
        if (f.type === 'multiselect') {
            return (
                <MultiSelect
                    value={Array.isArray(val) ? val : []}
                    options={f.options || []}
                    placeholder={f.placeholder || 'Select…'}
                    onChange={(next) => onChange?.(f.key, next)}
                />
            );
        }
        if (f.type === 'date') {
            return (
                <ReportDateInput
                    id={id}
                    value={val || ''}
                    max={f.max}
                    disabled={f.disabled}
                    placeholder={f.placeholder || 'Select date'}
                    onChange={(next) => onChange?.(f.key, next)}
                />
            );
        }
        if (f.type === 'month') {
            return (
                <input
                    id={id}
                    type={f.type}
                    className="v3-input"
                    value={val || ''}
                    max={f.max}
                    disabled={f.disabled}
                    onChange={(e) => onChange?.(f.key, e.target.value)}
                />
            );
        }
        if (f.type === 'text') {
            return (
                <input
                    id={id}
                    type="text"
                    className="v3-input"
                    value={val || ''}
                    placeholder={f.placeholder ? t(f.placeholder) : ''}
                    disabled={f.disabled}
                    onChange={(e) => onChange?.(f.key, e.target.value)}
                />
            );
        }
        // select
        return (
            <div className="v3-select-wrap">
                <select
                    id={id}
                    className="v3-input v3-select"
                    value={val || ''}
                    disabled={f.disabled}
                    onChange={(e) => onChange?.(f.key, e.target.value)}
                >
                    <option value="">{t(f.placeholder || 'All')}</option>
                    {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        );
    };

    const spanStyle = (f) => {
        if (f.span === 'full') return { gridColumn: '1 / -1' };
        if (f.span === 2) return { gridColumn: 'span 2' };
        return undefined;
    };

    return (
        <form className="v3-card v3-report-filters" onSubmit={submit}>
            <div className="v3-sadmin-form-grid-3">
                {fields.map((f) => (
                    <div className="v3-report-field" style={spanStyle(f)} key={f.key}>
                        {f.type === 'multiselect' ? (
                            <span className="v3-label">{t(f.label)}</span>
                        ) : (
                            <label className="v3-label" htmlFor={fieldId(f.key)}>{t(f.label)}</label>
                        )}
                        {renderControl(f)}
                        {f.role === 'endDate' && dateError && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{dateError}</span>
                        )}
                    </div>
                ))}
            </div>
            <div className="v3-report-filter-actions">
                <button type="submit" className="v3-btn v3-btn-primary" disabled={loading}>
                    <Play size={14} /> {loading ? t('Generating…') : t(generateLabel)}
                </button>
                <button type="button" className="v3-btn v3-btn-ghost" onClick={onReset} disabled={loading}>
                    <RotateCcw size={14} /> {t('Reset')}
                </button>
            </div>
        </form>
    );
};

export default ReportFilterBar;
